/**
 * PDF Generation Composable
 * Generates PDF documents from HTML content
 */
import { ref } from 'vue'

/**
 * Composable for generating PDF documents
 * @returns {Object} PDF generation methods and state
 */
export function usePdf() {
  const generating = ref(false)
  const error = ref(null)

  /**
   * Render HTML header to canvas image
   * @param {string} headerHtml - HTML for header
   * @returns {Promise<HTMLCanvasElement>} Canvas element
   */
  async function renderHeaderToCanvas(headerHtml) {
    const html2canvas = (await import('html2canvas')).default

    // Create temporary iframe for header
    const headerIframe = document.createElement('iframe')
    Object.assign(headerIframe.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '794px',
      height: '150px',
      border: 'none',
      opacity: '0.01',
      pointerEvents: 'none',
      zIndex: '-1',
    })
    document.body.appendChild(headerIframe)

    const headerDoc = headerIframe.contentDocument || headerIframe.contentWindow.document
    headerDoc.open()
    headerDoc.write(headerHtml)
    headerDoc.close()

    // Wait for header to load
    await new Promise((resolve) => setTimeout(resolve, 300))

    // Get actual header content height
    const headerElement = headerDoc.body.firstElementChild || headerDoc.body
    const actualHeight = headerElement.offsetHeight || 70

    // Render to canvas with actual content height only
    const canvas = await html2canvas(headerDoc.body, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: null, // Transparent background
      width: 794,
      height: actualHeight + 5, // Add small padding
    })

    // Cleanup
    document.body.removeChild(headerIframe)

    return canvas
  }

  /**
   * Generate PDF from HTML string (full HTML document)
   * @param {string} html - Full HTML document to convert to PDF
   * @param {string} filename - Output filename (without .pdf extension)
   * @param {Object} options - PDF generation options
   * @param {Function} headerGenerator - Optional function(pageNum, totalPages, dateStr) that returns header HTML
   * @returns {Promise<Blob>} PDF as Blob
   */
  async function generatePdfFromHtml(
    html,
    filename = 'document',
    options = {},
    headerGenerator = null
  ) {
    generating.value = true
    error.value = null

    let iframe = null

    try {
      // Dynamically import html2pdf
      const html2pdf = (await import('html2pdf.js')).default

      // Create iframe for rendering
      iframe = document.createElement('iframe')
      Object.assign(iframe.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '794px', // A4 at 96 DPI
        height: '1123px',
        border: 'none',
        opacity: '0.01', // Nearly invisible but still renders
        pointerEvents: 'none',
        zIndex: '-1',
      })
      document.body.appendChild(iframe)

      // Write content to iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
      iframeDoc.open()
      iframeDoc.write(html)
      iframeDoc.close()

      // Wait for iframe to fully load (fonts, styles)
      await new Promise((resolve) => {
        if (iframeDoc.readyState === 'complete') {
          setTimeout(resolve, 500)
        } else {
          iframe.onload = () => setTimeout(resolve, 500)
        }
      })

      // Get content from iframe body
      const element = iframeDoc.body

      // PDF options - add top margin for header (header is ~20mm tall)
      const topMargin = headerGenerator ? 24 : 5
      const defaultOptions = {
        margin: [topMargin, 5, 5, 5],
        filename: `${filename}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          foreignObjectRendering: false,
          removeContainer: false,
          backgroundColor: '#ffffff',
          windowWidth: 794,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
        },
        pagebreak: {
          mode: ['css'],
          avoid: ['tr'],
        },
      }

      const mergedOptions = { ...defaultOptions, ...options }

      // Generate PDF and get jsPDF instance
      const worker = html2pdf().set(mergedOptions).from(element)

      if (headerGenerator) {
        // Get PDF with jsPDF instance for adding headers
        const pdf = await worker.toPdf().get('pdf')
        const totalPages = pdf.internal.getNumberOfPages()
        const pageWidth = pdf.internal.pageSize.getWidth()

        // Get current date string
        const dateStr = new Date().toLocaleDateString('he-IL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })

        // Add header to each page
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i)

          // Generate header HTML for this page
          const headerHtml = headerGenerator(i, totalPages, dateStr)

          // Render header to canvas
          const headerCanvas = await renderHeaderToCanvas(headerHtml)

          // Add header image to page
          const headerImgData = headerCanvas.toDataURL('image/png')
          const headerHeight = (headerCanvas.height / headerCanvas.width) * pageWidth

          // Add at top of page (x=0, y=2mm from top)
          pdf.addImage(headerImgData, 'PNG', 0, 2, pageWidth, headerHeight)
        }

        // Output as blob
        const pdfBlob = pdf.output('blob')
        return pdfBlob
      } else {
        // No header - just output normally
        const pdfBlob = await worker.outputPdf('blob')
        return pdfBlob
      }
    } catch (err) {
      console.error('PDF generation error:', err)
      error.value = err.message
      throw err
    } finally {
      // Cleanup iframe
      if (iframe && iframe.parentNode) {
        document.body.removeChild(iframe)
      }
      generating.value = false
    }
  }

  /**
   * Generate PDF and return as base64 string (for email attachments)
   * @param {string} html - HTML content to convert to PDF
   * @param {string} filename - Output filename
   * @param {Object} options - PDF generation options
   * @param {Function} headerGenerator - Optional function(pageNum, totalPages, dateStr) that returns header HTML
   * @returns {Promise<string>} PDF as base64 string
   */
  async function generatePdfAsBase64(
    html,
    filename = 'document',
    options = {},
    headerGenerator = null
  ) {
    const blob = await generatePdfFromHtml(html, filename, options, headerGenerator)

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Generate PDF and trigger download
   * @param {string} html - HTML content to convert to PDF
   * @param {string} filename - Output filename
   * @param {Object} options - PDF generation options
   */
  async function downloadPdf(html, filename = 'document', options = {}) {
    const blob = await generatePdfFromHtml(html, filename, options)

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return {
    generating,
    error,
    generatePdfFromHtml,
    generatePdfAsBase64,
    downloadPdf,
  }
}
